/**
 * Mirrors of the Data Mapper explorer's and restore's wire shapes
 * (packages/api/src/services/data-mapper/data-plane/explorer-types.ts,
 * explorer-handlers.ts, restore.ts, data-mapper-events.ts and
 * data-mapper-explorer-reports/). Kept by hand, like dataMapper.types.ts.
 */

export const DM_EXPLORER_SCHEMA_PATH = "data-mapper-explorer-schema";
export const DM_EXPLORER_PROFILER_PATH = "data-mapper-explorer-profiler";
export const DM_EXPLORER_ANALYSIS_PATH = "data-mapper-explorer-analysis";
export const DM_EXPLORER_REPORTS_PATH = "data-mapper-explorer-reports";
export const DM_RESTORE_PATH = "data-mapper-restore";

/** The API's DM_EVENTS, by the same keys. */
export const DM_EVENTS = {
  progress: "explorer-progress",
  profileComplete: "explorer-profile-complete",
  profileError: "explorer-profile-error",
  analysisComplete: "explorer-analysis-complete",
  analysisError: "explorer-analysis-error",
  restoreProgress: "restore-progress",
  restoreComplete: "restore-complete",
  restoreError: "restore-error",
} as const;

export const PROFILER_EVENTS = [DM_EVENTS.progress, DM_EVENTS.profileComplete, DM_EVENTS.profileError] as const;
export const ANALYSIS_EVENTS = [DM_EVENTS.progress, DM_EVENTS.analysisComplete, DM_EVENTS.analysisError] as const;
export const RESTORE_EVENTS = [DM_EVENTS.restoreProgress, DM_EVENTS.restoreComplete, DM_EVENTS.restoreError] as const;

export type ProfilerEvent = (typeof PROFILER_EVENTS)[number];
export type AnalysisEvent = (typeof ANALYSIS_EVENTS)[number];
export type RestoreEvent = (typeof RESTORE_EVENTS)[number];

// ---- Schema ------------------------------------------------------------------

export interface ExplorerColumnInfo {
  name: string;
  dataType: string;
  maxLength: number | null;
  precision: number | null;
  scale: number | null;
  isNullable: boolean;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
  foreignKeyRef: { table: string; column: string } | null;
  defaultValue: string | null;
  isIdentity: boolean;
  isComputed: boolean;
}

export interface ExplorerIndexInfo {
  name: string;
  columns: string[];
  isUnique: boolean;
  isClustered: boolean;
  isPrimaryKey: boolean;
}

export interface ExplorerForeignKeyInfo {
  name: string;
  column: string;
  referencedTable: string;
  referencedColumn: string;
  schema: string;
  referencedSchema: string;
}

export interface ExplorerTableSchema {
  schema: string;
  name: string;
  fullName: string;
  columns: ExplorerColumnInfo[];
  indexes: ExplorerIndexInfo[];
  foreignKeys: ExplorerForeignKeyInfo[];
  rowCount: number;
  totalSizeKB: number;
  createdDate: string;
  modifiedDate: string;
}

// ---- Profiles ------------------------------------------------------------------

export type Severity = "info" | "warning" | "critical";

export interface DataQualityFlag {
  type: string;
  severity: Severity;
  message: string;
}

export interface ColumnProfile {
  name: string;
  dataType: string;
  totalCount: number;
  nullCount: number;
  nullPercent: number;
  distinctCount: number;
  cardinalityRatio: number;
  min?: number | string | null;
  max?: number | string | null;
  mean?: number | null;
  median?: number | null;
  stddev?: number | null;
  minLength?: number;
  maxLength?: number;
  avgLength?: number;
  topValues: { value: string; count: number; percent: number }[];
  patterns: { pattern: string; count: number; percent: number }[];
  flags: DataQualityFlag[];
}

export interface TableProfile {
  schema: string;
  tableName: string;
  fullName: string;
  rowCount: number;
  sampleSize: number;
  isSampled: boolean;
  samplePercent: number;
  columns: ColumnProfile[];
  sampleData: Record<string, unknown>[];
  profilingDurationMs: number;
  sampleFailed?: boolean;
  trimmed?: boolean;
}

// ---- Analysis ------------------------------------------------------------------

export interface TableClassification {
  domain: string;
  purpose: string;
  confidence: number;
  isLookupTable: boolean;
  isJunctionTable: boolean;
  isUIMapping: boolean;
  businessSummary: string;
}

export interface InferredRelationship {
  fromTable: string;
  fromColumn: string;
  toTable: string;
  toColumn: string;
  confidence: number;
  reasoning: string;
  type: string;
}

export interface DataQualityAssessment {
  overallScore: number;
  completeness: number;
  consistency: number;
  uniqueness: number;
  validity: number;
  issues: { table: string; column: string; issue: string; severity: Severity; recommendation: string }[];
}

export interface TableAnalysis {
  tableName: string;
  classification: TableClassification;
  qualityAssessment: DataQualityAssessment;
}

export interface DatabaseAnalysis {
  databaseName: string;
  analyzedAt: string;
  totalTables: number;
  totalRows: number;
  tables: TableAnalysis[];
  inferredRelationships: InferredRelationship[];
  domainSummary: string;
  schemaGroups: { domain: string; tables: string[] }[];
}

/** A background job's progress (`explorer-progress`, and `progress` in a status answer). */
export interface JobProgress {
  phase: string;
  table?: string;
  current: number;
  total: number;
  message: string;
  timestamp?: string;
}

export type JobStatus = "idle" | "running" | "completed" | "error";

/** A profiler or analysis `status` answer. */
export interface JobStatusAnswer {
  status: JobStatus;
  kind?: string;
  progress?: JobProgress | null;
  error?: string | null;
  hasResult?: boolean;
  profiledTables?: number;
}

/** The screen's view of one background job. */
export interface JobState {
  status: JobStatus;
  kind: string | null;
  progress: JobProgress | null;
  error: string | null;
}

export const IDLE_JOB: JobState = { status: "idle", kind: null, progress: null, error: null };

/** A job's progress as a whole percent, or null when it has no measure yet. */
export const progressPercent = (progress: JobProgress | null | undefined): number | null => {
  if (!progress || !(progress.total > 0)) return null;
  return Math.max(0, Math.min(100, Math.round((progress.current / progress.total) * 100)));
};

// ---- Reports ------------------------------------------------------------------

export interface ExplorerReportMeta {
  id: string;
  database: string;
  generatedAt: string;
  host: string;
  tableCount: number;
  totalRows: number;
  jsonSizeBytes: number;
  mdSizeBytes: number;
  scope: "profile" | "client" | "global";
  clientId: string | null;
  profileId: string | null;
  createdBy: number | null;
  createdAt: string;
}

export interface ExplorerReportListing {
  database: string;
  reports: ExplorerReportMeta[];
}

export interface ExplorerReport extends ExplorerReportMeta {
  report: unknown;
  markdown: string;
}

// ---- Restore ------------------------------------------------------------------

export interface RestoreJobView {
  id?: string;
  databaseName?: string;
  status: "idle" | "running" | "completed" | "error";
  phase?: "validating" | "reading-file-list" | "restoring" | "complete" | "error";
  percent?: number | null;
  startedAt?: string;
  completedAt?: string | null;
  error?: string | null;
  /** With `error`: database-exists, file-in-use, protected-database, restore-in-progress or db-error. */
  reason?: string | null;
}

export interface BakHeader {
  databaseName: string | null;
  backupType: number | string | null;
  backupStartDate: string | null;
  backupFinishDate: string | null;
  backupSize: number | null;
  compatibilityLevel: number | null;
  databaseVersion: number | null;
  collation: string | null;
}

export interface BakFile {
  logicalName: string | null;
  physicalName: string | null;
  type: string | null;
  sizeBytes: number | null;
}

export interface BakInfo {
  header: BakHeader[];
  fileList: BakFile[];
}

export interface ServerDatabase {
  name: string;
  state?: string | null;
  sizeMb?: number | null;
  createDate?: string | null;
}
